import React, { useState, useEffect } from "react";
import '../../assets/css/Dashboard.css';
import '../../App.css';
import { CSVLink } from "react-csv";
import { jsPDF } from 'jspdf';
import { useLocation } from "react-router-dom";
import * as XLSX from 'xlsx';
import LoadingSpinner from "../../utils/LoadingSpinner";
import { getLocationIncidents } from "../../utils/AWSAPI";
import { useTranslation } from 'react-i18next'; 
import i18next from 'i18next';
import i18n from "../Translations/i18n/i18n";  //import i18next

export default function Dashboard() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const storeValue = searchParams.get("location");
  const storeNameValue = searchParams.get("locationName");
  const { t } = useTranslation();

  // DATA API
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [openTickets, setOpenTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState([]);

  const [sortedData, setSortedData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [expandedTickets, setExpandedTickets] = useState({});

  // Are we showing a modal? I.E: Error, success.
  const [isModalShowing, setModalShowing] = useState(false);
  const [isModalUncloseable, setModalUncloseable] = useState(false);

  const [modalHeader, setModalHeader] = useState(t('loading'));
  const [modalBody, setModalBody] = useState(<></>);
  const [modalFooter, setModalFooter] = useState(<></>);

  const [activeTab, setActiveTab] = useState('open');

  const closeModal = () => {
    setModalShowing(false);
  };

  const Modal = () => {
    return (
      <form>
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h4>{modalHeader}</h4>
              {!isModalUncloseable && (
                <span className="close" onClick={closeModal}>&times;</span>
              )}
            </div>
            <div className="modal-body">{modalBody}</div>
            <div className="modal-footer">{modalFooter}</div>
          </div>
        </div>
      </form>
    );
  };

  const updateModal = (header, body, footer) => {
    setModalShowing(!isModalShowing);
    setModalHeader(header);
    setModalBody(body);
    setModalFooter(footer);
  }

  useEffect(() => {
    const loadLanguage = () => {
      const language = localStorage.getItem("language");
      if (language) {
        i18n.changeLanguage(language);
      }
    };
    loadLanguage();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const incidents = await getLocationIncidents(storeValue);
        if (incidents.Error) {
          return updateModal(t("whoops"), t("noIncidentsFound"));
        }
        setData(incidents);
        setLoading(false);
      } catch (err) {
        console.log(err);
      }
    }
    if (isLoading) {
      fetchData();
    }
  }, [isLoading]);

  useEffect(() => {
    if (data && data.data && data.data.Items) {
      const allIncidents = data.data.Items;
      const open = allIncidents.filter(incident => !['Resolved', 'Closed', 'Cancelled'].includes(incident.State.S));
      const closed = allIncidents.filter(incident => ['Resolved', 'Closed', 'Cancelled'].includes(incident.State.S));
      setOpenTickets(open);
      setClosedTickets(closed);
    }
  }, [data]);

  useEffect(() => {
    const mergedData = activeTab === 'open' ? openTickets : closedTickets;
    if (mergedData) {
      let sortedItems = [...mergedData];

      if (sortColumn) {
        if(sortColumn === "TicketID") {
          sortedItems.sort((a, b) => {
            const idA = parseInt(a.TicketID.S.match(/\d+/)[0]);
            const idB = parseInt(b.TicketID.S.match(/\d+/)[0]);
            return sortOrder === "asc" ? idA - idB : idB - idA;
          });          
        } else {
          sortedItems.sort((a, b) => {
            const keyA = a[sortColumn]?.S || a[sortColumn]?.N || "";
            const keyB = b[sortColumn]?.S || b[sortColumn]?.N || "";
  
            if (sortOrder === "asc") {
              return isNaN(keyA) ? keyA.localeCompare(keyB) : keyA - keyB;
            } else {
              return isNaN(keyB) ? keyB.localeCompare(keyA) : keyB - keyA;
            }
          });
        }
      }

      const filteredItems = sortedItems.filter(item =>
        Object.values(item)
          .some(value => value?.S?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setSortedData(filteredItems);
    }
  }, [activeTab, sortColumn, sortOrder, searchTerm, openTickets, closedTickets]);

  const handleCopyToClipboard = () => {
    const tempTable = document.createElement('table');
    const tempBody = tempTable.createTBody();
  
    sortedData.forEach(({ TicketID, ShortDescription, State, AgeOfTicket, Priority, AssignmentGroup }) => {
      const row = tempBody.insertRow();
      row.insertCell().textContent = TicketID.S;
      row.insertCell().textContent = ShortDescription.S;
      row.insertCell().textContent = State.S;
      row.insertCell().textContent = AgeOfTicket.S;
      row.insertCell().textContent = Priority.S;
      row.insertCell().textContent = AssignmentGroup.S;
    });
  
    document.body.appendChild(tempTable);
    const range = document.createRange();
    range.selectNodeContents(tempTable);
    window.getSelection().addRange(range);
    document.execCommand('copy');
    window.getSelection().removeAllRanges();
    document.body.removeChild(tempTable);

    alert(t('incidentsCopied'));
  };

  const handleExportExcel = () => {
    const table = document.getElementById('table-to-export');
    const ws = XLSX.utils.table_to_sheet(table);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'Incidents.xlsx');
  };

  const handleExportPDF = () => {
    const table = document.getElementById('table-to-export');
    const pdf = new jsPDF('p', 'pt', 'letter');
    pdf.html(table, { html2canvas: { scale: 0.45 } })
      .then(() => {
        pdf.save('Incidents.pdf');
      });
  };

  const transformDataForCSV = (data) => {
    return data.map((item) => {
      const transformedItem = {};
      for (const key in item) {
        if (Object.hasOwnProperty.call(item, key)) {
          transformedItem[key] = item[key].S || item[key].N || '';
        }
      }
      return transformedItem;
    });
  };  

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData?.slice(indexOfFirstItem, indexOfLastItem) || [];

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleButtonClick = (destination, storeValue, storeNameValue) => {
    window.location.href = `${destination}?location=${storeValue}&locationName=${storeNameValue}`;
  };

  const toggleDropdown = (ticketID) => {
    setExpandedTickets((prev) => ({
      ...prev,
      [ticketID]: !prev[ticketID],
    }));
  };  

  return (
    <div className="dashboard">
      <LoadingSpinner show={isLoading} />

      {isModalShowing && (
        <Modal />
      )}

      <div className="header">
        <h1>{storeNameValue}</h1>
        <div className="buttons-container">
          <button className="icons" onClick={() => handleButtonClick("new_incident", storeValue, storeNameValue)}>{t('newIncident')}</button>
          <button className="icons" onClick={() => handleButtonClick("new_rfm", storeValue, storeNameValue)}>{t('rfmPackageExport')}</button>
          <button className="icons" onClick={() => handleButtonClick("new_cap", storeValue, storeNameValue)}>{t('capRequest')}</button>
        </div>
      </div>

      {!isLoading && (
        data.data.Items ? (
          <div>
            <div className="recuadro">
              <body>
                <br />
                <div>
                  <div className="tabs">
                    <button className={`tab ${activeTab === 'open' ? 'active' : ''}`} onClick={() => handleTabChange('open')}>{t('openTickets')}</button>
                    <button className={`tab ${activeTab === 'closed' ? 'active' : ''}`} onClick={() => handleTabChange('closed')}>{t('closedTickets')}</button>
                  </div>
  
                  {currentItems.length > 0 ? (
                    <div>
                      <div className="menu-buttons">
                        <button className="save-as-bottom" onClick={handleCopyToClipboard}>{t('copy')}</button>
                        <button className="save-as-bottom" onClick={handleExportExcel}>{t('excel')}</button>
                        {sortedData && sortedData.length > 0 && (
                          <button className="save-as-bottom" >
                            <CSVLink
                              data={transformDataForCSV(sortedData)}
                              filename={"Incidents.csv"}
                              target="_blank"
                              className="csv"
                            >
                              {t('csv')}
                            </CSVLink>
                          </button>
                        )}
                        <button className="save-as-bottom" onClick={handleExportPDF}>{t('pdf')}</button>
      
                        <select
                          value={sortColumn}
                          onChange={(e) => setSortColumn(e.target.value)}
                          className="sort-select save-as-bottom"
                        >
                          <option value="null" selected disabled>{t('sortBy')}</option>
                          <option value="TicketID">{t('ticketID')}</option>
                          <option value="ShortDescription">{t('shortDescription')}</option>
                          <option value="State">{t('state')}</option>
                          <option value="AgeOfTicket">{t('ageOfTicket')}</option>
                          <option value="Priority">{t('Priority')}</option>
                          <option value="AssignmentGroup">{t('assignmentGroup')}</option>
                        </select>
      
                        <select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value)}
                          className="sort-select save-as-bottom hide"
                        >
                          <option value="null" selected disabled>{t('order')}</option>
                          <option value="asc">{t('ascending')}</option>
                          <option value="desc">{t('descending')}</option>
                        </select>
      
                        <input
                          type="text"
                          placeholder={t('searchIncident')}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="save-as-bottom"
                        />
                      </div>
                    
                      <div className="table-container">
                        <table id="table-to-export" className="Rest">
                          <thead>
                            <tr className="red">
                              <th>{t('ticketID')}</th>
                              <th>{t('shortDescription')}</th>
                              <th>{t('state')}</th>
                              <th>{t('ageOfTicket')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentItems.map(({ MCTicket, TicketID, AgeOfTicket, ShortDescription, State, Priority, AssignmentGroup }) => (
                              <React.Fragment key={MCTicket.S}>
                                <tr>
                                  <td>
                                    <button onClick={() => toggleDropdown(TicketID.S)} className="toggle-button">
                                      {expandedTickets[TicketID.S] ? '−' : '＋'}
                                    </button>
                                    <a className="hov2" href={`incident?ticketNumber=${TicketID.S}`}>{TicketID.S}</a>
                                  </td>
                                  <td>{ShortDescription.S}</td>
                                  <td style={{ fontWeight: (State.S === 'Resolved' || State.S === 'Closed') ? 'bold' : 'normal', color: State.S === 'Resolved' ? 'green' : State.S === 'Closed' ? 'darkred' : 'inherit' }}>{State.S}</td>
                                  <td>{AgeOfTicket.S}</td>
                                </tr>
                                {expandedTickets[TicketID.S] && (
                                  <tr>
                                    <td colSpan="6" className="dropdown">
                                      <div>
                                        <strong>{t('priority')}</strong> {Priority.S}
                                      </div>
                                      <div>
                                        <strong>{t('assignmentGroup')}</strong> {AssignmentGroup.S}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>  

                      <div className="pagination">
                        {Array.from({ length: Math.ceil(sortedData.length / itemsPerPage) }, (_, index) => (
                          <button key={index + 1} onClick={() => paginate(index + 1)}>
                            {index + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="no-announcements-banner">
                      <h3>{t('No Open Tickets')}</h3>
                      <p>{t('There are currently no registered incidents.')}</p>
                    </div>
                  )}
                </div>
              </body>
            </div>
          </div>
        ) : (
          <div className="no-announcements-banner">
            <h3>{t('No Tickets Found')}</h3>
            <p>{t('There are currently no registered incidents.')}</p>
          </div>      
        )
      )}
    </div>
  );
}